import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const C = { void:'#030A3C', void2:'#060D4A', void3:'#0A1260', cyan:'#00FFFF', cyanDim:'rgba(0,255,255,0.10)', cyanBorder:'rgba(0,255,255,0.15)', magma:'#FF5722', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)', green:'#2A8B7A', greenDim:'rgba(42,139,122,0.12)' };

const STAGES = [
  {
    n: '01',
    tag: 'Data entry',
    title: 'Upload from Claude Desktop or the dashboard',
    body: `There are two ways in. Use the MCP server in Claude Desktop to upload directly from a conversation - just tell Claude what file to process. Or upload through the SenseCritiq dashboard if you prefer a more traditional interface. Both routes support audio, video, and transcript files.`,
    detail: `Whichever entry point you use, the session ends up in the same place. Uploading the same file twice won't create a duplicate - we handle deduplication automatically.`,
    code: `// Option 1 - from Claude Desktop:
"Upload session-04.mp3 as
 'Onboarding exit interviews – March'"

// Option 2 - dashboard UI:
// Drag and drop → name your session
// → assign to a project → submit`,
  },
  {
    n: '02',
    tag: 'Transcription',
    title: 'Your recording is transcribed with speaker labels',
    body: `Once uploaded, your file is transcribed automatically. Each utterance is attributed to a speaker and timestamped to the second, so every quote that emerges from synthesis can be traced back to its exact moment in the original recording.`,
    detail: `Transcription runs in the background. You don't need to wait - you'll be notified when your session is ready.`,
    code: `// Transcript excerpt
{
  "speaker": "Speaker B",
  "text": "I kept hitting that wall every time
           I tried to invite someone.",
  "start": "2:22",
  "end":   "2:26"
}`,
  },
  {
    n: '03',
    tag: 'Filtering',
    title: 'Signal is separated from noise',
    body: `Not everything in a research recording is useful. Greetings, filler phrases, interviewer scaffolding, and off-topic tangents are identified and stripped before synthesis begins. What remains is the part that actually matters - the participant's real responses.`,
    detail: `This filtering step is what keeps synthesis accurate and fast. Less content in means cleaner, more focused outputs.`,
    code: `// Each utterance is classified as:
// "signal"  → passes to synthesis
// "noise"   → discarded
// "meta"    → discarded

// Only signal reaches the next step`,
  },
  {
    n: '04',
    tag: 'Synthesis',
    title: 'SenseCritiq extracts themes, findings, and quotes',
    body: `The filtered transcript is synthesised into structured output: themes, key findings, and verbatim supporting quotes. Every finding is required to cite its source - speaker, timestamp, and session - before it can be returned. No unsourced claims make it through.`,
    detail: `Synthesis works on the cleaned signal only, not the full transcript. This keeps outputs focused and citation-accurate.`,
    code: `// Synthesis output (abbreviated)
{
  "themes": [{
    "label": "Invitation friction",
    "findings": [{
      "statement": "The invite flow breaks when
        the invitee already has an account.",
      "quote": {
        "text": "I kept hitting that wall...",
        "speaker": "Speaker B",
        "timestamp": "2:22"
      }
    }]
  }]
}`,
  },
  {
    n: '05',
    tag: 'Query & export',
    title: 'Search, ask, and share - your way',
    body: `Once a session is ready, your insights are searchable. Ask questions in plain language from Claude Desktop, or browse and filter through the dashboard. Export a formatted report as Markdown, PDF, or directly to a Notion page. Every output - whether a search result or a report - includes the source citations.`,
    detail: `Your research memory compounds over time. The more sessions you run, the richer the answers you get from a single question.`,
    code: `// Ask from Claude Desktop:
"What do users say about inviting
 collaborators across all sessions?"

// Or export from the dashboard:
// → Download PDF
// → Download Markdown
// → Push to Notion`,
  },
];

export default function HowItWorks() {
  const [active, setActive] = useState(0);
  const stage = STAGES[active];

  return (
    <div style={s.root}>
      <MarketingNav />

      {/* Hero */}
      <div style={s.hero} className="scq-hero">
        <div style={s.heroBg} />
        <div style={s.eyebrow}>Under the hood</div>
        <h1 style={s.h1}>How SenseCritiq works</h1>
        <p style={s.heroSub}>Five stages from raw recording to queryable research memory.</p>
      </div>

      {/* Interactive pipeline */}
      <div style={s.pipelineWrap} className="scq-section">
        <div style={s.pipelineInner}>
          {/* Stage nav */}
          <div style={s.stageNav} className="scq-stage-nav">
            {STAGES.map((st, i) => (
              <button
                key={st.n}
                style={{...s.stageBtn, ...(active === i ? s.stageBtnActive : {})}}
                className={`scq-stage-btn${active === i ? ' scq-stage-btn-active' : ''}`}
                onClick={() => setActive(i)}
              >
                <span style={{...s.stageBtnN, ...(active === i ? s.stageBtnNActive : {})}}>{st.n}</span>
                <span style={s.stageBtnLabel}>{st.tag}</span>
              </button>
            ))}
          </div>

          {/* Stage detail */}
          <div style={s.stageDetail} className="scq-stage-detail">
            <div style={s.stageLeft}>
              <div style={s.stageTag}>{stage.tag}</div>
              <h2 style={s.stageTitle}>{stage.title}</h2>
              <p style={s.stageBody}>{stage.body}</p>
              <div style={s.stageNote}>
                <span style={s.stageNoteIcon}>⟡</span>
                <span style={s.stageNoteText}>{stage.detail}</span>
              </div>
            </div>
            <div style={s.stageRight}>
              <div style={s.codeCard}>
                <div style={s.codeDots}>
                  <span style={{...s.codeDot, background:'#FF5F57'}} />
                  <span style={{...s.codeDot, background:'#FFBD2E'}} />
                  <span style={{...s.codeDot, background:'#28CA41'}} />
                </div>
                <pre style={s.code}>{stage.code}</pre>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={s.progressBar}>
            {STAGES.map((_, i) => (
              <div
                key={i}
                style={{...s.progressSegment, ...(i <= active ? s.progressSegmentActive : {})}}
                onClick={() => setActive(i)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={s.bottomCta}>
        <div style={s.bottomInner} className="scq-bottom-inner">
          <h2 style={s.bottomH2}>Ready to try it?</h2>
          <p style={s.bottomSub}>Two free sessions. No credit card required.</p>
          <div style={s.bottomBtns}>
            <Link to="/sign-up" style={s.ctaPrimary}>Get started free</Link>
            <Link to="/features" style={s.ctaGhost}>Explore features →</Link>
          </div>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },
  hero: { textAlign:'center', padding:'100px 24px 80px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,255,255,0.07) 0%, transparent 65%)', pointerEvents:'none' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, position:'relative' },
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(36px,5vw,64px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.05, marginBottom:20, position:'relative' },
  heroSub: { fontSize:18, color:C.muted, maxWidth:480, margin:'0 auto', lineHeight:1.65, position:'relative' },

  pipelineWrap: { padding:'60px 24px 80px' },
  pipelineInner: { maxWidth:1100, margin:'0 auto' },
  stageNav: { display:'flex', gap:4, marginBottom:40, background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)', border:`1px solid ${C.border}`, borderRadius:14, padding:6, boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  stageBtn: { flex:1, background:'none', border:'none', borderRadius:10, padding:'12px 8px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4, transition:'background 0.15s' },
  stageBtnActive: { background:C.void3 },
  stageBtnN: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:10, fontWeight:700, letterSpacing:'0.1em', color:C.muted2 },
  stageBtnNActive: { color:C.cyan },
  stageBtnLabel: { fontSize:12, fontWeight:600, color:C.muted, fontFamily:"'Satoshi','Inter',sans-serif" },

  stageDetail: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:56, marginBottom:32, alignItems:'start' },
  stageLeft: {},
  stageTag: { fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:C.cyan, marginBottom:12 },
  stageTitle: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(22px,2.5vw,32px)', fontWeight:700, letterSpacing:'-0.02em', lineHeight:1.2, marginBottom:16 },
  stageBody: { fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:24 },
  stageNote: { background:C.greenDim, border:`1px solid rgba(42,139,122,0.2)`, borderRadius:10, padding:'14px 16px', display:'flex', gap:10, alignItems:'flex-start' },
  stageNoteIcon: { color:C.green, flexShrink:0, fontSize:14, marginTop:1 },
  stageNoteText: { fontSize:13, color:'rgba(248,249,250,0.75)', lineHeight:1.6 },
  stageRight: {},
  codeCard: { background:'rgba(2,8,24,0.90)', backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)', border:`1px solid rgba(0,255,255,0.12)`, borderRadius:14, padding:'18px 20px', boxShadow:'0 4px 24px rgba(0,0,0,0.60), inset 0 1px 0 rgba(0,255,255,0.08), inset 0 0 0 1px rgba(0,255,255,0.03)' },
  codeDots: { display:'flex', gap:6, marginBottom:14 },
  codeDot: { width:9, height:9, borderRadius:'50%' },
  code: { margin:0, fontSize:12.5, color:'rgba(248,249,250,0.72)', lineHeight:1.75, overflowX:'auto', fontFamily:"'JetBrains Mono','Fira Code',monospace" },

  progressBar: { display:'flex', gap:4 },
  progressSegment: { flex:1, height:3, borderRadius:2, background:C.border, cursor:'pointer', transition:'background 0.2s' },
  progressSegmentActive: { background:C.cyan },

  archWrap: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'80px 24px' },
  archInner: { maxWidth:900, margin:'0 auto', textAlign:'center' },
  sectionEyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16 },
  sectionH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(24px,3vw,38px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:16 },
  archP: { fontSize:16, color:C.muted, lineHeight:1.75, marginBottom:48, maxWidth:660, margin:'0 auto 48px' },
  archDiagram: { display:'flex', alignItems:'center', justifyContent:'center', gap:8, flexWrap:'wrap' },
  archNode: { background:'linear-gradient(135deg, rgba(10,18,96,0.70) 0%, rgba(14,22,110,0.75) 100%)', backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)', border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 18px', textAlign:'center', minWidth:120, boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  archNodeAccent: { border:`1px solid ${C.cyanBorder}`, background:C.cyanDim },
  archNodeLabel: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:13, fontWeight:700, marginBottom:4 },
  archNodeSub: { fontSize:11, color:C.muted2 },
  archArrow: { fontSize:16, color:C.muted2 },

  bottomCta: { padding:'80px 24px 100px', textAlign:'center' },
  bottomInner: { maxWidth:560, margin:'0 auto' },
  bottomH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,40px)', fontWeight:700, letterSpacing:'-0.03em', marginBottom:14 },
  bottomSub: { fontSize:16, color:C.muted, marginBottom:32 },
  bottomBtns: { display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' },
  ctaPrimary: { display:'inline-block', padding:'14px 30px', borderRadius:10, background:C.magma, color:'#fff', fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:15, textDecoration:'none' },
  ctaGhost: { display:'inline-block', padding:'14px 26px', borderRadius:10, background:'rgba(255,255,255,0.05)', color:C.muted, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:600, fontSize:15, textDecoration:'none', border:`1px solid ${C.border}` },
};
